/**
 * Inlined from `br-mask@0.0.10` (https://www.npmjs.com/package/br-mask).
 * The original package is unmaintained and ships only View-Engine artifacts,
 * which break under Angular 16 (no more ngcc). The behavior is preserved
 * verbatim; only the wrapping was rewritten as a modern Ivy-compatible
 * TypeScript directive.
 *
 * Original author: Antonio Marques <tmowna@gmail.com>
 * Original license: MIT
 */
import {
  Directive,
  ElementRef,
  Host,
  HostListener,
  Injectable,
  Input,
  OnInit,
  Optional,
  SkipSelf,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroupDirective } from '@angular/forms';

export class BrMaskModel {
  form?: AbstractControl | null;
  mask?: string;
  len?: number;
  person?: boolean;
  phone?: boolean;
  phoneNotDDD?: boolean;
  money?: boolean;
  percent?: boolean;
  type?: 'alfa' | 'num' | 'all' = 'alfa';
  numberAndTousand?: boolean = false;
  decimal?: number = 2;
  decimalCaracter?: string = ',';
  thousand?: string;
  userCaracters?: boolean = false;
  moneyInitHasInt?: boolean = true;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[brmasker]',
})
@Injectable()
export class BrMaskDirective implements OnInit {
  @Input() brmasker: BrMaskModel = new BrMaskModel();
  @Input() formControlName: string;

  constructor(
    @Optional() @Host() @SkipSelf() private controlContainer: FormGroupDirective,
    private elementRef: ElementRef
  ) {}

  @HostListener('keyup', ['$event'])
  inputKeyup(event: any): void {
    const value = this.returnValue(event.target.value);
    this.setValueInFormControl(value);
  }

  @HostListener('ngModelChange', ['$event'])
  onNgModelChange(e: string): void {
    const value = this.returnValue(e);
    if (value) {
      this.setValueInFormControl(value, false);
    }
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle -- inlined br-mask library, @Injectable retained verbatim
  ngOnInit(): void {
    if (!this.brmasker.type) {
      this.brmasker.type = 'all';
    }
    if (!this.brmasker.decimal) {
      this.brmasker.decimal = 2;
    }
    if (this.brmasker.moneyInitHasInt === undefined) {
      this.brmasker.moneyInitHasInt = true;
    }
    if (!this.brmasker.decimalCaracter) {
      this.brmasker.decimalCaracter = ',';
    }
    if (this.controlContainer) {
      if (this.formControlName) {
        this.brmasker.form = this.controlContainer.control.get(this.formControlName);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Missing FormControlName directive from host element of the component');
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn("Can't find parent FormGroup directive");
    }
    this.initialValue();
  }

  initialValue(): void {
    const value = this.returnValue(this.elementRef.nativeElement.value);
    this.setValueInFormControl(value);
  }

  verifyFormControl(): boolean {
    return this.brmasker.form instanceof FormControl;
  }

  setValueInFormControl(value: string, emitViewToModelChange?: boolean): void {
    if (!this.verifyFormControl()) {
      this.elementRef.nativeElement.value = value;
      return;
    }
    (this.brmasker.form as FormControl).setValue(value, { emitViewToModelChange });
    (this.brmasker.form as FormControl).updateValueAndValidity();
  }

  writeCreateValue(value: string, config: BrMaskModel = new BrMaskModel()): string {
    if (value && config.phone) {
      return value.replace(
        /^(?:(?:\+|00)?(55)\s?)?(?:\(?([1-9][0-9])\)?\s?)?(?:((?:9\d|[2-9])\d{3})\-?(\d{4}))$/gi,
        '$1 ($2) $3-$4'
      );
    }
    if (value && config.phoneNotDDD) {
      return this.phoneNotDDDMask(value);
    }
    if (value && config.money) {
      return this.writeValueMoney(value, config);
    }
    if (value && (config as any).person) {
      return this.writeValuePerson(value);
    }
    if (value && config.percent) {
      return this.writeValuePercent(value);
    }
    if (this.brmasker.userCaracters) {
      return this.usingSpecialCharacters(value, this.brmasker.mask, this.brmasker.len);
    }
    if (value && config.mask) {
      this.brmasker.mask = config.mask;
      if (config.len) {
        this.brmasker.len = config.len;
      }
      return this.onInput(value);
    }
    return value;
  }

  writeValuePercent(value: string): string {
    value.replace(/\D/gi, '');
    value.replace(/%/gi, '');
    return value.replace(/([0-9]{0})$/gi, '%$1');
  }

  writeValuePerson(value: string): string {
    if (value.length <= 11) {
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/gi, '$1.$2.$3-$4');
    }
    return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/gi, '$1.$2.$3/$4-$5');
  }

  writeValueMoney(value: string, config: BrMaskModel = new BrMaskModel()): string {
    return this.moneyMask(value, config);
  }

  returnValue(value: string): string {
    if (!this.brmasker.mask) {
      this.brmasker.mask = '';
    }
    if (value) {
      let formValue = value;
      if (this.brmasker.type === 'alfa') {
        formValue = formValue.replace(/\d/gi, '');
      }
      if (this.brmasker.type === 'num') {
        formValue = formValue.replace(/\D/gi, '');
      }
      if (this.brmasker.money) {
        return this.moneyMask(this.onInput(formValue), this.brmasker);
      }
      if (this.brmasker.phone) {
        return this.phoneMask(formValue);
      }
      if (this.brmasker.phoneNotDDD) {
        return this.phoneNotDDDMask(formValue);
      }
      if ((this.brmasker as any).person) {
        return this.peapollMask(formValue);
      }
      if (this.brmasker.percent) {
        return this.percentMask(formValue);
      }
      if (this.brmasker.numberAndTousand) {
        return this.thousand(formValue);
      }
      if (this.brmasker.userCaracters) {
        return this.usingSpecialCharacters(formValue, this.brmasker.mask, this.brmasker.len);
      }
      return this.onInput(formValue);
    }
    return '';
  }

  applyCpfMask(formValue: string): string {
    formValue = formValue.replace(/\D/gi, '');
    formValue = formValue.replace(/(\d{3})(\d)/gi, '$1.$2');
    formValue = formValue.replace(/(\d{3})(\d)/gi, '$1.$2');
    formValue = formValue.replace(/(\d{3})(\d{1,2})$/gi, '$1-$2');
    return formValue;
  }

  applyCnpjMask(formValue: string): string {
    formValue = formValue.replace(/\D/gi, '');
    formValue = formValue.replace(/(\d{2})(\d)/gi, '$1.$2');
    formValue = formValue.replace(/(\d{3})(\d)/gi, '$1.$2');
    formValue = formValue.replace(/(\d{3})(\d)/gi, '$1/$2');
    formValue = formValue.replace(/(\d{4})(\d{1,4})$/gi, '$1-$2');
    formValue = formValue.replace(/(\d{2})(\d{1,2})$/gi, '$1$2');
    return formValue;
  }

  percentMask(value: string): string {
    let tmp = value;
    tmp = tmp.replace(/\D/gi, '');
    tmp = tmp.replace(/%/gi, '');
    tmp = tmp.replace(/([0-9]{0})$/gi, '%$1');
    return tmp;
  }

  phoneMask(value: string): string {
    let formValue = value;
    if (formValue.length > 14 || formValue.length === 11) {
      this.brmasker.len = 15;
      this.brmasker.mask = '(99) 99999-9999';
      formValue = formValue.replace(/\D/gi, '');
      formValue = formValue.replace(/(\d{2})(\d)/gi, '$1 $2');
      formValue = formValue.replace(/(\d{5})(\d)/gi, '$1-$2');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1$2');
    } else {
      this.brmasker.len = 14;
      this.brmasker.mask = '(99) 9999-9999';
      formValue = formValue.replace(/\D/gi, '');
      formValue = formValue.replace(/(\d{2})(\d)/gi, '$1 $2');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1-$2');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1$2');
    }
    return this.onInput(formValue);
  }

  phoneNotDDDMask(value: string): string {
    let formValue = value;
    if (formValue.length > 9) {
      this.brmasker.len = 10;
      this.brmasker.mask = '99999-9999';
      formValue = formValue.replace(/\D/gi, '');
      formValue = formValue.replace(/(\d{5})(\d)/gi, '$1-$2');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1$2');
    } else {
      this.brmasker.len = 9;
      this.brmasker.mask = '9999-9999';
      formValue = formValue.replace(/\D/gi, '');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1-$2');
      formValue = formValue.replace(/(\d{4})(\d)/gi, '$1$2');
    }
    return this.onInput(formValue);
  }

  peapollMask(value: string): string {
    let formValue = value;
    if (formValue.length >= 14) {
      if (formValue.length === 14 && formValue.indexOf('-') > 0) {
        this.brmasker.len = 14;
        this.brmasker.mask = '999.999.999-99';
        formValue = this.applyCpfMask(formValue);
      } else {
        this.brmasker.len = 18;
        this.brmasker.mask = '99.999.999/9999-99';
        formValue = this.applyCnpjMask(formValue);
      }
    } else {
      this.brmasker.len = 14;
      this.brmasker.mask = '999.999.999-99';
      formValue = this.applyCpfMask(formValue);
    }
    return this.onInput(formValue);
  }

  moneyMask(value: string, config: BrMaskModel): string {
    const decimal = (config.decimal || this.brmasker.decimal) as number;
    const decimalCaracter = config.decimalCaracter || ',';
    value = value.replace(/\D/gi, '').replace(new RegExp('([0-9]{' + decimal + '})$', 'g'), decimalCaracter + '$1');
    if (value.length === 1 && !this.brmasker.moneyInitHasInt) {
      const dec = Array(decimal - 1).fill(0);
      return '0' + decimalCaracter + dec.join('') + value;
    }
    if (value.length === decimal + 1) {
      return '0' + value;
    } else if (value.length > decimal + 2 && value.charAt(0) === '0') {
      return value.substr(1);
    }
    if (config.thousand && value.length > Number(4) + Number(config.decimal)) {
      const valueOne = '([0-9]{3})' + decimalCaracter + '([0-9]{' + config.decimal + '}$)';
      value = value.replace(new RegExp('' + valueOne, 'g'), config.thousand + '$1' + decimalCaracter + '$2');
    }
    if (config.thousand && value.length > Number(8) + Number(config.decimal)) {
      const valueTwo =
        '([0-9]{3})' + config.thousand + '([0-9]{3})' + decimalCaracter + '([0-9]{' + config.decimal + '}$)';
      value = value.replace(
        new RegExp('' + valueTwo, 'g'),
        config.thousand + '$1' + config.thousand + '$2' + decimalCaracter + '$3'
      );
    }
    return value;
  }

  onInput(value: string): string {
    return this.formatField(value, this.brmasker.mask, this.brmasker.len);
  }

  usingSpecialCharacters(field: string, mask: string, size: number): string {
    if (!size) {
      size = 99999999999;
    }
    let boleanoMascara: boolean;
    const exp = /\-|\.|\,| /gi;
    const campoSoNumeros = field.toString().replace(exp, '');
    let posicaoCampo = 0;
    let NovoValorCampo = '';
    let sizeMascara = campoSoNumeros.length;
    for (let i = 0; i < sizeMascara; i++) {
      if (i < size) {
        boleanoMascara = mask.charAt(i) === '-' || mask.charAt(i) === '.' || mask.charAt(i) === ',';
        if (boleanoMascara) {
          NovoValorCampo += mask.charAt(i);
          sizeMascara++;
        } else {
          NovoValorCampo += campoSoNumeros.charAt(posicaoCampo);
          posicaoCampo++;
        }
      }
    }
    return NovoValorCampo;
  }

  thousand(value: string): string {
    const val = value.replace(/\D/gi, '');
    const reverse = val.toString().split('').reverse().join('');
    const thousands = reverse.match(/\d{1,3}/g);
    if (thousands) {
      return thousands
        .join('' + (this.brmasker.thousand || '.'))
        .split('')
        .reverse()
        .join('');
    }
    return '';
  }

  formatField(field: string, mask: string, size: number): string {
    if (!size) {
      size = 99999999999;
    }
    let boleanoMascara: boolean;
    const exp = /\_|\-|\.|\/|\(|\)|\,|\*|\+|\@|\#|\$|\&|\%|\:| /gi;
    const campoSoNumeros = field.toString().replace(exp, '');
    let posicaoCampo = 0;
    let NovoValorCampo = '';
    let TamanhoMascara = campoSoNumeros.length;
    for (let i = 0; i < TamanhoMascara; i++) {
      if (i < size) {
        boleanoMascara = mask.charAt(i) === '-' || mask.charAt(i) === '.' || mask.charAt(i) === '/';
        boleanoMascara = boleanoMascara || mask.charAt(i) === '_';
        boleanoMascara = boleanoMascara || mask.charAt(i) === '(' || mask.charAt(i) === ')' || mask.charAt(i) === ' ';
        boleanoMascara = boleanoMascara || mask.charAt(i) === ',' || mask.charAt(i) === '*' || mask.charAt(i) === '+';
        boleanoMascara = boleanoMascara || mask.charAt(i) === '@' || mask.charAt(i) === '#' || mask.charAt(i) === ':';
        boleanoMascara = boleanoMascara || mask.charAt(i) === '$' || mask.charAt(i) === '&' || mask.charAt(i) === '%';
        if (boleanoMascara) {
          NovoValorCampo += mask.charAt(i);
          TamanhoMascara++;
        } else {
          NovoValorCampo += campoSoNumeros.charAt(posicaoCampo);
          posicaoCampo++;
        }
      }
    }
    return NovoValorCampo;
  }
}
